/**
 * useSettings hook - fetches organization settings from the API
 * and provides them app-wide via React context.
 *
 * Settings are persisted in the org's JSONB settings column.
 * Missing keys fall back to backend-supplied defaults.
 */
import { createContext, useContext, useState, useCallback } from 'react';
import apiClient from '@/lib/api-client';
import type { MeasurementSystem, TemperatureUnit } from '@/lib/units';

export interface OrgSettings {
  measurement_units: MeasurementSystem;
  temperature_unit: TemperatureUnit;
  date_format: string;
  time_format: string;
  language: string;
  theme: string;
  density: string;
  default_view: string;
  currency: string;
  notifications_email_orders: boolean;
  notifications_email_inventory: boolean;
  notifications_email_deliveries: boolean;
  notifications_email_reports: boolean;
  notifications_push_orders: boolean;
  notifications_push_alerts: boolean;
  notifications_push_deliveries: boolean;
  notifications_push_iot: boolean;
  notifications_digest: string;
  [key: string]: unknown;
}

const DEFAULT_SETTINGS: OrgSettings = {
  measurement_units: 'metric',
  temperature_unit: 'celsius',
  date_format: 'YYYY-MM-DD',
  time_format: '24h',
  language: 'en',
  theme: 'light',
  density: 'comfortable',
  default_view: 'full',
  currency: 'USD',
  notifications_email_orders: true,
  notifications_email_inventory: true,
  notifications_email_deliveries: true,
  notifications_email_reports: false,
  notifications_push_orders: true,
  notifications_push_alerts: true,
  notifications_push_deliveries: true,
  notifications_push_iot: true,
  notifications_digest: 'daily',
};

interface SettingsContextType {
  settings: OrgSettings;
  isLoading: boolean;
  error: string | null;
  updateSettings: (patch: Partial<OrgSettings>) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

export const SettingsContext = createContext<SettingsContextType>({
  settings: DEFAULT_SETTINGS,
  isLoading: false,
  error: null,
  updateSettings: async () => {},
  refreshSettings: async () => {},
});

export function useSettingsProvider() {
  const [settings, setSettings] = useState<OrgSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await apiClient.get('/api/v1/org/settings');
      if (res.data?.success && res.data?.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...res.data.data });
      }
    } catch (err: any) {
      // If org endpoints fail (e.g. demo mode), keep defaults silently
      console.warn('Failed to load settings, using defaults:', err.message);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateSettings = useCallback(async (patch: Partial<OrgSettings>) => {
    try {
      setError(null);
      const res = await apiClient.patch('/api/v1/org/settings', patch);
      if (res.data?.success && res.data?.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...res.data.data });
      }
    } catch (err: any) {
      const msg = err.response?.data?.detail || err.message || 'Failed to save settings';
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    refreshSettings: fetchSettings,
    fetchOnMount: fetchSettings,
  };
}

export function useSettings(): SettingsContextType {
  return useContext(SettingsContext);
}
